<template>
  <div class="-mx-[1.5rem] mt-[16px]">
    <b-table
      :items="dataFetcher.data"
      :fields="CREATOR_DATA_FIELDS"
      :empty-text="emptyTextLabel"
      responsive
      show-empty
      small
      sticky-header
      class="!max-h-[calc(100vh-330px)] !mb-0"
      :tbody-tr-class="{'hover:bg-slate-100 transition-colors duration-300 cursor-pointer': dataFetcher.data.length}"
      @row-clicked="openDrawer"
    >
      <template #cell(id)="data">
        {{ data.index + 1 }}
      </template>
      <template #cell(name)="{ item }">
        <div class="flex flex-col gap-[10px]">
          <span class="flex items-center gap-[4px]">{{ item.name }}<span class="k-export text-[16px] text-[#08A0F7]" /></span>
          <span class="text-[12px] text-[#828282]">{{ item.tiktok_username }}</span>
        </div>
      </template>
      <template #cell(phone)="{ value }">
        <span class="flex items-center gap-[4px]">
          {{ value }} <span
            class="k-copy text-[16px] text-[#626262] hover:bg-[#E2E2E2] rounded-[4px] p-[4px]"
            @click="copyPhone($event, value)"
          />
        </span>
      </template>
      <template #cell(status)="{ item }">
        <status-pill :status="item.status" />
      </template>
      <template #cell(category)="{ item: { category } }">
        <category-pill :category="category" />
      </template>
      <template #custom-foot>
        <td colspan="100%">
          <load-more-data
            v-if="dataFetcher"
            :fetcher="dataFetcher"
          />
        </td>
      </template>
      <template #empty="{emptyText}">
        <empty-table-card
          :fetcher="dataFetcher"
          :label="emptyText"
        />
      </template>
    </b-table>
    <member-drawer
      ref="memberDrawer"
      :data="drawerData"
      @saved="onCreatorSaved"
    />
  </div>
</template>

<script>
import { createPaginatedFetcher } from '@/@core/libs/fetch/usePaginatedFetcher'
import { DAY_MONTH_YEAR } from '@/libs/filterDate'
import { CREATOR_DATA_FIELDS } from '../config'
import StatusPill from './StatusPill.vue'
import MemberDrawer from '../../member/components/MemberDrawer.vue'
import LoadMoreData from '../LoadMoreData.vue'
import EmptyTableCard from '../EmptyTableCard.vue'
import CategoryPill from '../CategoryPill.vue'
import { parseAge, parsePhone, parseStatus } from '../utils'

export default {
  name: 'TableComponent',
  components: {
    MemberDrawer,
    LoadMoreData,
    StatusPill,
    EmptyTableCard,
    CategoryPill,
  },
  props: {
    filter: {
      type: Object,
      default: () => ({
        keyword: '',
        category: [],
      }),
    },
  },
  data() {
    return {
      dataFetcher: null,
      CREATOR_DATA_FIELDS,
      drawerData: null,
    }
  },
  computed: {
    emptyTextLabel() {
      if (this.dataFetcher.isLoading) return 'Memuat data...'
      if (this.dataFetcher.error) return 'Terdapat kesalahan saat memuat data'
      return 'Tidak ada data yang ditampilkan.'
    },
  },
  created() {
    this.initializeFetcher()
  },
  methods: {
    /**
     * Inisialisasi paginated fetcher untuk daftar kreator
     */
    initializeFetcher() {
      const fnFetch = ({
        offset, limit, category, search, startDate, endDate, status,
      }) => this.$http_new.get('/komclass/api/v1/komlabs/creators', {
        params: {
          offset,
          limit,
          category,
          search,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          status,
        },
      }).then(res => {
        const { creators, counts } = res.data.data
        this.$emit('set-user-count', { ...counts, suspend: counts.off })
        return {
          data: creators,
        }
      }).catch(() => {
        if (this.dataFetcher.offset === 0) {
          throw new Error('Gagal memuat data')
        }
        return {
          data: [],
        }
      })

      this.dataFetcher = createPaginatedFetcher(fnFetch, 0, 20)
    },
    /**
     * Reset dan muat ulang data tabel dengan filter baru
     * @param {Object} params
     * @param {string[]} [params.category]
     * @param {string} [params.keyword]
     * @param {string} [params.start_date]
     * @param {string} [params.end_date]
     * @param {string} [params.status]
     */
    refreshData({
      category, keyword, start_date, end_date, status,
    }) {
      let finalStatus = status === 'semua' ? undefined : status
      if (finalStatus === 'suspend') finalStatus = 'off'
      const finalCategory = category?.length ? category.join(',') : undefined

      this.dataFetcher.fetchData({
        reset: true,
        options: {
          category: finalCategory,
          search: keyword || undefined,
          startDate: start_date,
          endDate: end_date,
          status: finalStatus,
        },
      })
    },
    /**
     * Buka drawer detail kreator
     * @param {{ id: number|string }} item
     */
    openDrawer(item) {
      const mapper = data => ({
        'Data Diri': {
          Nama: data.name,
          Email: data.email,
          'No HP': parsePhone(data.phone),
          'Link Sosmed': data.tiktok_username ? `https://www.tiktok.com/@${data.tiktok_username}` : '-',
          Kategori: data.category?.join(', '),
          'Tanggal Register': DAY_MONTH_YEAR(data.register_date),
        },
        'Lain-lain': {
          'GMV 30 hari terakhir': data.gmv_30_days,
          'Jumlah Followers': data.followers_count,
          Domisili: data.domicile || '-',
          Gender: data.gender || '-',
          Usia: parseAge(data.age),
          'Apakah sudah join dengan MCN affiliate lain?': data.mcn_interest || '-',
          'Kreator Status': parseStatus(data.status),
        },
      })
      this.$refs.memberDrawer.openDrawer(item.id, mapper)
    },
    /**
     * Dipanggil setelah edit kreator berhasil — refresh tabel dari awal
     * dengan mempertahankan filter yang sedang aktif.
     */
    onCreatorSaved() {
      this.refreshData({
        category: this.filter.category,
        keyword: this.filter.keyword,
        start_date: this.filter.start_date,
        end_date: this.filter.end_date,
        status: this.filter.status,
      })
    },
    /**
     * Salin nomor HP ke clipboard (dengan prefix 0)
     * @param {Event} e
     * @param {string|number} phone
     */
    copyPhone(e, phone) {
      e.preventDefault()
      e.stopPropagation()
      navigator.clipboard.writeText(phone)
      this.$toast_success({ message: 'Nomor HP berhasil disalin' })
    },
  },
}
</script>
